/* Copyright (c) Trimble Inc. All rights reserved. */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useUserStore, getUserProfile } from '../../stores/User.store';
import { UserAvatarModel, UserAvatarProps } from './UserAvatar.model';
import { Logger } from '@tcp-base/toolkit';
import { UserProfile } from '../../models/User.model';

/**
 * UserAvatar Controller
 *
 * Contains all business logic for UserAvatar component.
 * Receives data from UserStore and props, returns processed model for View.
 */

/**
 * UserAvatar Controller Hook
 *
 * Uses Zustand store with Dexie live sync for real-time user data updates.
 * Automatically syncs when user data changes in IndexedDB.
 *
 * @param props - Component props
 * @returns UserAvatarModel for the View
 */
export const useUserAvatarController = ({
  size = 'md',
  className = '',
  showName = false,
  namePosition = 'right',
  fallbackInitials,
  fallbackImageUrl,
  onClick,
  loadingText = 'Loading...',
  errorText = '',
}: UserAvatarProps): UserAvatarModel => {
  // Local state for user data and loading
  const [userData, setUserData] = useState(getUserProfile());
  const [isLoading, setIsLoading] = useState(true);
  const userProfile = useUserStore((state): UserProfile => state.userProfile);
  const isError = false; // Zustand store doesn't have error state like React Query

  // Local state for image error handling
  const [imageError, setImageError] = useState(false);
  // Local state for tracking actual image loading (after URL is received)
  const [imageLoading, setImageLoading] = useState(false);

  // Load user data on component mount and when getUserData changes
  useEffect(() => {
    const loadUserData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setUserData(userProfile);
        // If there's a picture URL, set image loading state
        try {
          if (userProfile?.picture) {
            setImageLoading(true);
            setImageError(false); // Reset image error when URL changes
          }
        } catch (pictureError) {
          Logger.error('Failed to access user picture property:', pictureError);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [userProfile]);

  /**
   * Generate initials from user's name
   */
  const generatedInitials = useMemo((): string => {
    // Only use fallbackInitials if explicitly provided and not empty/space
    if (fallbackInitials && fallbackInitials.trim() && fallbackInitials !== ' ') {
      return fallbackInitials;
    }

    if (!userData || Object.keys(userData).length === 0) {
      return errorText.substring(0, 2).toUpperCase();
    }

    const firstName = userData.given_name || '';
    const lastName = userData.family_name || '';

    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    } else if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    } else if (userData.email) {
      return userData.email.substring(0, 2).toUpperCase();
    }

    return errorText.substring(0, 2).toUpperCase();
  }, [userData, fallbackInitials, errorText]);

  /**
   * Generate display name from user data
   */
  const displayName = useMemo((): string => {
    if (!userData || Object.keys(userData).length === 0) {
      return errorText;
    }

    const { given_name, family_name, email } = userData;

    if (given_name && family_name) {
      return `${given_name} ${family_name}`;
    } else if (given_name) {
      return given_name;
    } else if (family_name) {
      return family_name;
    } else if (email) {
      return email;
    }

    return errorText;
  }, [userData, errorText, isLoading]);

  /**
   * Handle image load error
   */
  const handleImageError = useCallback((): void => {
    Logger.warn('UserAvatar: Failed to load user picture, falling back to initials');
    setImageError(true);
    setImageLoading(false); // Stop showing loader on error
  }, []);

  /**
   * Handle image load success
   */
  const handleImageLoad = useCallback(() => {
    setImageError(false);
    setImageLoading(false); // Image fully loaded, hide loader
  }, []);

  /**
   * Handle click events
   */
  const handleClick = useMemo((): (() => void) | undefined => {
    return onClick;
  }, [onClick]);

  /**
   * Handle keyboard events for accessibility
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  // Safely access picture property with error handling
  const pictureUrl = useMemo((): string | null => {
    try {
      return userData?.picture || null;
    } catch (error) {
      Logger.error('Failed to access user picture:', error);
      return null;
    }
  }, [userData]);

  // Return the complete model for the View
  return {
    // Display properties
    displayName,
    generatedInitials,
    shouldShowImage: !!pictureUrl && !imageError && !isLoading,
    imageUrl: pictureUrl,
    shouldShowFallbackImage: !!fallbackImageUrl && imageError && !!pictureUrl,
    fallbackImageUrl: fallbackImageUrl || null,

    // State flags
    isLoading,
    hasError: isError,
    hasImageError: imageError,
    imageLoading, // Track image loading state

    // Event handlers
    handleImageError,
    handleImageLoad,
    handleClick,
    handleKeyDown: onClick ? handleKeyDown : undefined,

    // Props passed through
    size,
    className,
    showName,
    namePosition,
    loadingText,
    errorText,
  };
};
